import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import AdminLayout from '../components/Layout/AdminLayout';
import Button from '../components/UI/Button';
import Modal from '../components/UI/Modal';
import { hallsAPI } from '../services/api';
import { Plus, CreditCard as Edit2, Trash2, Building, Users, DollarSign, Settings } from 'lucide-react';

const Halls = () => {
  const [halls, setHalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingHall, setEditingHall] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  useEffect(() => {
    fetchHalls();
  }, []);

  const fetchHalls = async () => {
    try {
      const response = await hallsAPI.getAll();
      setHalls(response.data);
    } catch (error) {
      toast.error('فشل في تحميل القاعات');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('الرجاء اختيار صورة صحيحة');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('حجم الصورة يجب أن يكون أقل من 5 ميجابايت');
        return;
      }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('https://api.hananmanan.wecodeforyou.io/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('فشل في رفع الصورة');
      }

      const data = await response.json();
      return data.url;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const handleCreateEdit = async (data) => {
    try {
      setUploadingImage(true);

      // Build payload with proper type conversions
      const payload = {
        name: data.name,
        description: data.description || '',
        capacity: Number(data.capacity),
        pricePerHour: Number(data.pricePerHour),
      };

      // Only include isAvailable when updating (backend doesn't accept it on create)
      if (editingHall) {
        payload.isAvailable = Boolean(data.isAvailable);
      }

      // Convert amenities string to array
      if (data.amenities && typeof data.amenities === 'string' && data.amenities.trim()) {
        payload.amenities = data.amenities.split(',').map(item => item.trim()).filter(item => item);
      } else if (Array.isArray(data.amenities)) {
        payload.amenities = data.amenities;
      }

      // Upload image if selected
      if (selectedImage) {
        const imageUrl = await uploadImage(selectedImage);
        payload.imageUrl = imageUrl;
      } else if (editingHall && editingHall.imageUrl) {
        payload.imageUrl = editingHall.imageUrl;
      }

      if (editingHall) {
        await hallsAPI.update(editingHall.id, payload);
        toast.success('تم تحديث القاعة بنجاح');
      } else {
        await hallsAPI.create(payload);
        toast.success('تم إنشاء القاعة بنجاح');
      }

      fetchHalls();
      setIsModalOpen(false);
      setEditingHall(null);
      setSelectedImage(null);
      setImagePreview(null);
      reset();
    } catch (error) {
      console.error('Error creating/updating hall:', error);
      const errorMsg = error.response?.data?.message || (editingHall ? 'فشل في تحديث القاعة' : 'فشل في إنشاء القاعة');
      toast.error(errorMsg);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleEdit = (hall) => {
    setEditingHall(hall);
    setImagePreview(hall.imageUrl || null);
    setSelectedImage(null);
    reset({
      name: hall.name,
      description: hall.description,
      capacity: hall.capacity,
      pricePerHour: hall.pricePerHour,
      isAvailable: hall.isAvailable,
      amenities: hall.amenities ? hall.amenities.join(', ') : '',
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await hallsAPI.delete(id);
      toast.success('تم حذف القاعة بنجاح');
      fetchHalls();
      setDeleteConfirm(null);
    } catch (error) {
      toast.error('فشل في حذف القاعة');
    }
  };

  const toggleAvailability = async (hall) => {
    try {
      await hallsAPI.update(hall.id, { isAvailable: !hall.isAvailable });
      toast.success('تم تحديث حالة القاعة');
      fetchHalls();
    } catch (error) {
      toast.error('فشل في تحديث حالة القاعة');
    }
  };

  const openCreateModal = () => {
    setEditingHall(null);
    setSelectedImage(null);
    setImagePreview(null);
    reset();
    setIsModalOpen(true);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-gold"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text-primary">إدارة القاعات</h1>
            <p className="text-text-secondary">إدارة قاعات المسجد والتحكم في توفرها</p>
          </div>
          <Button onClick={openCreateModal}>
            <Plus className="w-4 h-4 ml-2" />
            قاعة جديدة
          </Button>
        </div>

        {/* Halls Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {halls.map((hall) => (
            <div key={hall.id} className="bg-card rounded-xl overflow-hidden border border-divider">
              {hall.imageUrl && (
                <img 
                  src={hall.imageUrl} 
                  alt={hall.name}
                  className="w-full h-48 object-cover"
                />
              )}
              
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 space-x-reverse mb-2">
                      <h3 className="text-lg font-semibold text-text-primary">
                        {hall.name}
                      </h3>
                      <div className="flex items-center">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={hall.isAvailable}
                            onChange={() => toggleAvailability(hall)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent-gold/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-gold"></div>
                        </label>
                      </div>
                    </div>
                    <div className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                      hall.isAvailable 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {hall.isAvailable ? 'متاحة' : 'غير متاحة'}
                    </div>
                  </div>
                  
                  <div className="flex space-x-2 space-x-reverse">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(hall)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteConfirm(hall)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>

                {hall.description && (
                  <p className="text-text-secondary text-sm mb-4 line-clamp-2">
                    {hall.description}
                  </p>
                )}

                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center text-text-muted">
                      <Users className="w-4 h-4 ml-2" />
                      السعة
                    </div>
                    <span className="text-text-primary font-medium">
                      {hall.capacity} شخص
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center text-text-muted">
                      <DollarSign className="w-4 h-4 ml-2" />
                      السعر/الساعة
                    </div>
                    <span className="text-text-primary font-medium">
                      {hall.pricePerHour} جنيه مصري
                    </span>
                  </div>

                  {hall.amenities && hall.amenities.length > 0 && (
                    <div className="pt-2">
                      <div className="flex items-center text-text-muted text-sm mb-2">
                        <Settings className="w-4 h-4 ml-2" />
                        المرافق
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {hall.amenities.map((amenity, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-accent-gold/20 text-accent-gold text-xs rounded-full"
                          >
                            {amenity}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {halls.length === 0 && (
          <div className="text-center py-12">
            <Building className="mx-auto h-12 w-12 text-text-muted mb-4" />
            <h3 className="text-lg font-medium text-text-primary mb-2">لا توجد قاعات</h3>
            <p className="text-text-muted">ابدأ بإنشاء قاعة جديدة للمسجد</p>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingHall(null);
          reset();
        }}
        title={editingHall ? 'تعديل القاعة' : 'قاعة جديدة'}
        size="lg"
      >
        <form onSubmit={handleSubmit(handleCreateEdit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                اسم القاعة *
              </label>
              <input
                {...register('name', { required: 'اسم القاعة مطلوب' })}
                className="w-full px-3 py-2 bg-primary-light border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="مثال: القاعة الكبرى"
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                السعة *
              </label>
              <input
                type="number"
                {...register('capacity', {
                  required: 'السعة مطلوبة',
                  min: { value: 1, message: 'السعة يجب أن تكون أكبر من 0' },
                  valueAsNumber: true
                })}
                className="w-full px-3 py-2 bg-primary-light border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="100"
              />
              {errors.capacity && (
                <p className="text-red-500 text-sm mt-1">{errors.capacity.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                السعر بالجنيه مصري/الساعة *
              </label>
              <input
                type="number"
                step="0.01"
                {...register('pricePerHour', {
                  required: 'السعر مطلوب',
                  min: { value: 0, message: 'السعر لا يمكن أن يكون سالباً' },
                  valueAsNumber: true
                })}
                className="w-full px-3 py-2 bg-primary-light border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="50.00"
              />
              {errors.pricePerHour && (
                <p className="text-red-500 text-sm mt-1">{errors.pricePerHour.message}</p>
              )}
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                {...register('isAvailable')}
                className="w-4 h-4 text-accent-gold bg-primary-light border-divider rounded focus:ring-accent-gold"
                defaultChecked
              />
              <label className="mr-2 text-sm text-text-primary">
                القاعة متاحة للحجز
              </label>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-text-primary mb-2">
                وصف القاعة
              </label>
              <textarea
                {...register('description')}
                rows="3"
                className="w-full px-3 py-2 bg-primary-light border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="وصف تفصيلي للقاعة..."
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-text-primary mb-2">
                المرافق المتوفرة
              </label>
              <input
                {...register('amenities')}
                className="w-full px-3 py-2 bg-primary-light border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="مثال: تكييف، نظام صوتي، بروجكتر (افصل بالفاصلة)"
              />
              <p className="text-xs text-text-muted mt-1">
                افصل كل مرفق بفاصلة
              </p>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-text-primary mb-2">
                صورة القاعة
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="w-full px-3 py-2 bg-primary-light border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold file:ml-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-accent-gold file:text-white hover:file:bg-accent-gold/90"
              />
              <p className="text-xs text-text-muted mt-1">
                الحد الأقصى لحجم الملف: 5 ميجابايت
              </p>
              {imagePreview && (
                <div className="mt-3">
                  <img
                    src={imagePreview}
                    alt="معاينة"
                    className="w-full h-48 object-cover rounded-lg border border-divider"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex space-x-3 space-x-reverse justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setIsModalOpen(false);
                setEditingHall(null);
                setSelectedImage(null);
                setImagePreview(null);
                reset();
              }}
            >
              إلغاء
            </Button>
            <Button type="submit" loading={uploadingImage}>
              {editingHall ? 'تحديث' : 'إنشاء'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="تأكيد الحذف"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-text-secondary">
            هل أنت متأكد من حذف القاعة "{deleteConfirm?.name}"؟
          </p>
          <div className="flex space-x-3 space-x-reverse justify-end">
            <Button
              variant="secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              إلغاء
            </Button>
            <Button
              variant="danger"
              onClick={() => handleDelete(deleteConfirm.id)}
            >
              حذف
            </Button>
          </div>
        </div>
      </Modal>
    </AdminLayout>
  );
};

export default Halls;